import { useEffect, useState } from 'react'
import { addUpdateHandler } from '../../utils'
import { PaymentImport } from './Import'
const {
    RoutesMap, useNavigate, useSearchParams, useDispatch, SessionManager,
    Loader, ButtonLoader, getPaymentOrderStatus, sidebar,
    ShieldCheck, RefreshCw, CircleCheck, CircleX, Clock, CircleAlert,
    Button, Card, logo,
} = PaymentImport

const PAID = ['paid', 'success', 'successful', 'completed', 'captured']
const FAILED = ['failed', 'failure', 'rejected']
const CANCELLED = ['cancelled', 'canceled']
const HOLD = ['hold']

const normalizePhase = (value) => {
    const status = String(value || '').trim().toLowerCase()
    if (PAID.includes(status)) return 'success'
    if (FAILED.includes(status)) return 'failure'
    if (CANCELLED.includes(status)) return 'cancelled'
    if (HOLD.includes(status)) return 'hold'
    return 'pending'
}

const readUser = () => {
    try {
        return JSON.parse(SessionManager.getCookie('user') || '{}')
    } catch (error) {
        return {}
    }
}

const ownedBySession = (record) => {
    const rowUserId = record?.user_id ?? record?.user?.id
    if (rowUserId == null) return true
    const sessionUser = readUser()
    if (sessionUser?.id == null) return true
    return String(rowUserId) === String(sessionUser.id)
}

export default function PaymentResult() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const orderId = searchParams.get('order_id') || ''
    const [loading, setLoading] = useState(true)
    const [phase, setPhase] = useState('pending')
    const [record, setRecord] = useState(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const [continuing, setContinuing] = useState(false)

    useEffect(() => {
        if (!SessionManager.getCookie('user_token')) {
            navigate(RoutesMap.LOGIN.path)
            return
        }
        if (!orderId) {
            setRecord(null)
            setPhase('unknown')
            setLoading(false)
            return
        }

        let cancelled = false
        let timer
        let attempts = 0

        const load = () => {
            dispatch(getPaymentOrderStatus({ order_id: orderId })).then((res) => {
                if (cancelled) return
                const payload = res?.payload
                const httpStatus = payload?.status
                const raw = payload?.data?.data ?? payload?.data
                const nextRecord = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null
                const statusValue = nextRecord?.payment_status || nextRecord?.status

                if (httpStatus === 401) {
                    addUpdateHandler({
                        res: payload,
                        navigate,
                        success_toast: '',
                        wrong_toast: '',
                        navigate_route: '',
                        spinner: setLoading,
                        toggle: () => {},
                    })
                    return
                }

                if (httpStatus === 200 && nextRecord && statusValue && ownedBySession(nextRecord)) {
                    setRecord(nextRecord)
                    const nextPhase = normalizePhase(statusValue)
                    setPhase(nextPhase)
                    setLoading(false)
                    if (nextPhase === 'pending' && attempts < 4) {
                        attempts += 1
                        timer = setTimeout(load, 3000)
                    }
                    return
                }

                if (!httpStatus || httpStatus >= 500) {
                    setRecord(null)
                    setPhase('pending')
                    setLoading(false)
                    return
                }

                setRecord(null)
                setPhase('unknown')
                setLoading(false)
            })
        }

        setLoading(true)
        load()
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [dispatch, navigate, orderId, refreshKey])

    const paymentType = record?.payment_type || ''
    const isRegistration = paymentType === 'Registration Fee'
    const isExam = paymentType === 'Exam Fee'
    const reference = record?.razorpay_order_id || record?.order_id || orderId
    const registrationPending = ['pending', 'hold'].includes(String(readUser()?.payment_status || '').toLowerCase())

    useEffect(() => {
        if (phase !== 'success' || !isRegistration) return
        const user = readUser()
        if (user?.payment_status === 'Paid') return
        SessionManager.setCookie('user', JSON.stringify({ ...user, payment_status: 'Paid' }), 3)
    }, [phase, isRegistration])

    const goNext = () => {
        if (isExam) {
            navigate(RoutesMap.EXAM_LIST.path)
            return
        }
        setContinuing(true)
        dispatch(sidebar({ navigate })).finally(() => {
            setContinuing(false)
            navigate(RoutesMap.DASHBOARD.path)
        })
    }

    const goRetry = () => {
        if (isExam) {
            navigate(RoutesMap.EXAM_LIST.path)
            return
        }
        if (isRegistration || registrationPending) {
            navigate(RoutesMap.PAYMENT.path)
            return
        }
        navigate(RoutesMap.DASHBOARD.path)
    }

    const view = phase === 'success'
        ? {
            title: 'Payment successful',
            message: isExam
                ? 'Your exam payment is confirmed. You can return to your exams.'
                : isRegistration
                    ? 'Your registration payment is confirmed. You can continue to the dashboard.'
                    : 'Your payment is confirmed.',
            color: '#0f766e',
            icon: <CircleCheck size={28} color="#0f766e" />,
        }
        : phase === 'failure'
            ? {
                title: 'Payment failed',
                message: 'The payment was not completed. You can try again when you are ready.',
                color: '#b91c1c',
                icon: <CircleX size={28} color="#b91c1c" />,
            }
            : phase === 'cancelled'
                ? {
                    title: 'Payment cancelled',
                    message: 'This payment was cancelled. Nothing has been marked as paid.',
                    color: '#334155',
                    icon: <CircleAlert size={28} color="#334155" />,
                }
                : phase === 'hold'
                    ? {
                        title: 'Payment on hold',
                        message: 'This payment is on hold. It is not marked as paid.',
                        color: '#9a6b00',
                        icon: <Clock size={28} color="#9a6b00" />,
                    }
                    : phase === 'unknown'
                    ? {
                        title: 'Payment not confirmed',
                        message: 'We could not confirm this payment. Refresh the status, or go back and try again if you were not charged.',
                        color: '#334155',
                        icon: <CircleAlert size={28} color="#334155" />,
                    }
                    : {
                        title: 'Confirming payment',
                        message: 'We are still confirming this payment with the server. Refresh before starting another payment.',
                        color: '#b45309',
                        icon: <Clock size={28} color="#b45309" />,
                    }

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center p-3" style={{
            background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 50%, #f8fafc 100%)',
        }}>
            <div style={{ width: '100%', maxWidth: 520 }}>
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="text-center p-4" style={{ background: view.color }}>
                        <img src={logo} alt="logo" style={{ height: 56, filter: 'brightness(0) invert(1)' }} />
                        <h4 className="text-white mt-3 mb-1">{view.title}</h4>
                        <p className="mb-0" style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>
                            {view.message}
                        </p>
                    </div>
                    <Card.Body className="p-4">
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-3" style={{ background: '#f8fafc' }}>
                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: '#f1f5f9' }}>
                                        {view.icon}
                                    </div>
                                    <div>
                                        <div className="fw-semibold" style={{ color: '#0f172a' }}>{view.title}</div>
                                        {paymentType ? (
                                            <div className="text-muted" style={{ fontSize: 13 }}>{paymentType}</div>
                                        ) : null}
                                    </div>
                                </div>

                                {reference ? (
                                    <div className="d-flex justify-content-between align-items-center mb-4" style={{ fontSize: 14 }}>
                                        <span className="text-muted">Order reference</span>
                                        <span className="fw-medium" style={{ wordBreak: 'break-all', marginLeft: 12 }}>{reference}</span>
                                    </div>
                                ) : null}

                                <div className="d-flex align-items-center gap-2 mb-3 text-muted" style={{ fontSize: 12 }}>
                                    <ShieldCheck size={16} color="#0f766e" />
                                    Status is confirmed from your payment record.
                                </div>

                                {phase === 'success' ? (
                                    <Button
                                        className="w-100 d-flex align-items-center justify-content-center gap-2 border-0"
                                        style={{ background: '#0f766e', height: 44 }}
                                        disabled={continuing}
                                        onClick={goNext}
                                    >
                                        {continuing ? <ButtonLoader /> : (isExam ? 'Go to exams' : 'Continue')}
                                    </Button>
                                ) : null}

                                {phase === 'failure' || phase === 'cancelled' || phase === 'hold' ? (
                                    <Button
                                        className="w-100 border-0"
                                        style={{ background: '#0f766e', height: 44 }}
                                        onClick={goRetry}
                                    >
                                        {isExam ? 'Back to exams' : (isRegistration || registrationPending ? 'Try again' : 'Back to dashboard')}
                                    </Button>
                                ) : null}

                                {phase === 'pending' || phase === 'unknown' ? (
                                    <Button
                                        className="w-100 d-flex align-items-center justify-content-center gap-2 border-0 mb-2"
                                        style={{ background: '#0f766e', height: 44 }}
                                        onClick={() => setRefreshKey((value) => value + 1)}
                                    >
                                        <RefreshCw size={16} /> Refresh status
                                    </Button>
                                ) : null}

                                {phase !== 'success' ? (
                                    <div className="text-center mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm text-muted text-decoration-none"
                                            onClick={() => navigate(registrationPending && !isExam ? RoutesMap.PAYMENT.path : RoutesMap.DASHBOARD.path)}
                                        >
                                            {registrationPending && !isExam ? 'Back to payment' : 'Back to dashboard'}
                                        </button>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    )
}
